import React, { useEffect, useState } from "react";
import { LogoIcon } from "./LogoIcon";
import { GoogleIcon } from "./GoogleIcon";
import { Textfield } from "./Textfield";
import contentData from "./data/content.json";
import emailContent, { setupSteps } from "./data/content";
import "./setupPane.css";
import { generateRandomArrayWithSum } from "../../utils/utils";
import { useNavigate } from "react-router-dom";
import Terms from "./Terms";

interface EmailEntryScreenProps {
  email: string;
  hasError: boolean;
  errorText: string;
  isSetupScreen: boolean;
  onEmailChange: (email: string) => void;
  onContinue: () => void;
}

// Primary Button Component
const PrimaryButton: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  return (
    <button
      className="primary-button"
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        onClick(e);
      }}
    >
      <span className="primary-button-text">{contentData.auth.buttonText}</span>
    </button>
  );
};

// Google Button Component
const GoogleButton: React.FC = () => {
  return (
    <button className="secondary-button">
      <div className="google-icon">
        <GoogleIcon width={20} height={20} />
      </div>
      <span className="secondary-button-text">
        {contentData.auth.googleButtonText}
      </span>
    </button>
  );
};

// Divider Component
const Divider: React.FC = () => {
  return (
    <div className="divider">
      <div className="divider-line" />
    </div>
  );
};

// Resend Text Component
const ResendText: React.FC<{ hasError: boolean; errorText: string }> = ({
  hasError,
  errorText,
}) => {
  const textColor = hasError ? "#e5484d" : "#84828e";
  const resendText = hasError
    ? errorText
      ? errorText
      : emailContent.email.errorText
    : emailContent.email.resendText;

  return (
    <div className="resend-text" style={{ color: textColor }}>
      <span>{resendText} </span>
    </div>
  );
};

// Auth Form Component
const AuthForm: React.FC<EmailEntryScreenProps> = ({
  email,
  hasError,
  errorText,
  onEmailChange,
  onContinue,
}) => {
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email.trim()) {
      onContinue();
    }
  };

  return (
    <div className="auth-form-container">
      <div className="auth-title">{contentData.auth.title}</div>
      <Divider />
      <form onSubmit={handleSubmit} className="form-group">
        <Textfield
          label={contentData.auth.emailLabel}
          placeholder={contentData.auth.emailPlaceholder}
          value={email}
          onChange={onEmailChange}
          type="text"
        />
        {hasError && <ResendText hasError={hasError} errorText={errorText} />}

        <PrimaryButton onClick={onContinue} />
        {/* <div className="or-text">
          {contentData.auth.orText}
        </div> */}
        {/* <GoogleButton /> */}
      </form>
    </div>
  );
};

const SVGTick = () => {
  return (
    <svg className="progress-icon" fill="none" viewBox="0 0 24 24">
      <path className="check-icon" d="M20 7L9 18L4 13" />
    </svg>
  );
};

const SVGCircle = () => {
  return (
    <svg className="progress-icon" fill="none" viewBox="0 0 24 24">
      <path
        className="circle-icon"
        d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
      />
    </svg>
  );
};
{
  /* <SVGTick></SVGTick>
<SVGCircle></SVGCircle> */
}

const SetupPane: React.FC<EmailEntryScreenProps> = () => {
  const [completedIndex, setIndex] = useState(0);
  const navigate = useNavigate();
  useEffect(() => {
    const SetupTimer = async () => {
      const randomArray = generateRandomArrayWithSum(10, 5);

      function chaneVal(seconds) {
        return new Promise((resolve) => {
          setTimeout(() => {
            setIndex((prevCount) => prevCount + 1);
            resolve();
          }, seconds * 1000);
        });
      }

      for (let i = 0; i < randomArray.length; i++) {
        await chaneVal(randomArray[i]);
      }
    };

    SetupTimer();
  }, []);

  useEffect(() => {
    if (completedIndex == setupSteps.length - 1) {
      navigate("/home");
    }
  }, [completedIndex]);

  return (
    <>
      <div className="setup-content">
        <h1 className="setup-title">Setting up your account</h1>

        <div className="divider"></div>

        <div className="progress-list">
          {setupSteps.map((step, index) => {
            const isCompleted = index <= completedIndex;

            return (
              <div className="progress-item" key={step}>
                {isCompleted ? <SVGTick></SVGTick> : <SVGCircle></SVGCircle>}
                <span
                  className={`progress-text ${
                    isCompleted ? "completed" : "pending"
                  }`}
                >
                  {step}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

// Email Entry Screen Component
export const EmailEntryScreen: React.FC<EmailEntryScreenProps> = (props) => {
  return (
    <div className="auth-panel">
      {props.isSetupScreen ? <SetupPane {...props} /> : <AuthForm {...props} />}
      <Terms />
    </div>
  );
};

export default EmailEntryScreen;
